import { Button, Dropdown, Modal, SelectDropdownOption } from '@/components';
import { queryClient } from '@/constants';
import {
  CompanyKeys,
  useGetEligibleCompaniesByInfrastructureCost,
} from '@/modules/Companies/queries';
import {
  useAttachCompanyInfrastructureCost,
  useAttachOrganizationInfrastructureCost,
} from '@/modules/Infrastructure/queries';
import { InfrastructureCostKey } from '@/modules/Infrastructure/queries/types';
import {
  AttachCompanyInfrastructureCostModel,
  AttachOrganizationInfrastructureCostModel,
} from '@/modules/Infrastructure/types';
import { useGetAllOrganizations } from '@/modules/Organizations/queries';
import { OrganizationKeys } from '@/modules/Organizations/queries/keys';
import { getDropdownArray } from '@/utils/get-dropdown-array.util';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

//TODO: Refactor this into splitted components based on the context prop.
interface AttachEntityToInfrastructureCostModalProps {
  costId: number;
  context: 'organization' | 'company';
}

export const AttachEntityToInfrastructureCostModal = ({
  costId,
  context,
}: AttachEntityToInfrastructureCostModalProps): ReactElement => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [dropdownEntityValue, setDropdownEntityValue] = useState<number>();
  const handleModalState = () => setIsOpen(!isOpen);

  const { mutate: mutateOrganization, isLoading: isLoadingOrganization } =
    useAttachOrganizationInfrastructureCost();
  const { mutate: mutateCompany, isLoading: isLoadingCompany } =
    useAttachCompanyInfrastructureCost();

  const handleConfirm = () => {
    if (context === 'organization') {
      if (!dropdownEntityValue) {
        return;
      }
      const payload: AttachOrganizationInfrastructureCostModel = {
        infrastructureCostId: costId,
        organizationId: dropdownEntityValue,
      };
      mutateOrganization(payload, {
        onError: () => {
          toast.error(
            t('error_attaching_entity_to_cost', { entity: t('organization') }),
          );
        },
        onSuccess: () => {
          queryClient.invalidateQueries(InfrastructureCostKey.ALL);
          queryClient.invalidateQueries(OrganizationKeys.ALL);
          queryClient.invalidateQueries(CompanyKeys.ALL);
          handleModalState();
          toast.success(
            t('success_attaching_entity_to_cost', {
              entity: t('organization'),
            }),
          );
        },
      });
    } else if (context === 'company') {
      if (!dropdownEntityValue) {
        return;
      }
      const payload: AttachCompanyInfrastructureCostModel = {
        infrastructureCostId: costId,
        companyId: dropdownEntityValue,
      };
      mutateCompany(payload, {
        onError: () => {
          toast.error(
            t('error_attaching_entity_to_cost', { entity: t('company') }),
          );
        },
        onSuccess: () => {
          queryClient.invalidateQueries(InfrastructureCostKey.LIST());
          queryClient.invalidateQueries(CompanyKeys.ALL);
          handleModalState();
          toast.success(
            t('success_attaching_entity_to_cost', { entity: t('company') }),
          );
        },
      });
    }
  };

  const { data: organizationsData } = useGetAllOrganizations();
  const { data: companiesData } =
    useGetEligibleCompaniesByInfrastructureCost(costId);

  const entityDropdownOptions = useMemo(() => {
    if (context === 'organization') {
      return getDropdownArray(organizationsData || []);
    } else {
      return getDropdownArray(companiesData || []);
    }
  }, [context, organizationsData, companiesData]);

  const handleOnSelectEntityDropdownValue = useCallback(
    (option: SelectDropdownOption<number>) =>
      setDropdownEntityValue(option.value),
    [],
  );

  const isLoading =
    context === 'organization' ? isLoadingOrganization : isLoadingCompany;
  const buttonText =
    context === 'organization' ? t('add_organization') : t('add_company');

  const modalTitle =
    context === 'organization'
      ? t('associate_organization')
      : t('associate_company');

  useEffect(() => {
    if (context === 'organization' && organizationsData?.length == 1) {
      setDropdownEntityValue(organizationsData[0].id);
    }

    if (context === 'company' && companiesData?.length == 1) {
      setDropdownEntityValue(companiesData[0].id);
    }
  }, [companiesData, context, organizationsData]);

  return (
    <>
      <Button onClick={handleModalState} variant="primary">
        {buttonText}
      </Button>
      <Modal
        isOpen={isOpen}
        title={modalTitle}
        className="h-fit w-128"
        handleClose={handleModalState}
      >
        <div className="mt-6 flex flex-col gap-5">
          <div className="flex flex-col gap-2">
            <div className="text-sm font-bold capitalize">
              {t('select_existing_entity', { entity: t(context) })}
            </div>
            <Dropdown
              className="h-10 w-full rounded-md bg-brightGray capitalize italic"
              placeholder={t('select')}
              options={entityDropdownOptions}
              value={dropdownEntityValue}
              onSelect={handleOnSelectEntityDropdownValue}
            />
          </div>
          <div className="mt-8 flex justify-between">
            <Button
              isDisabled={isLoading}
              onClick={handleModalState}
              variant="outline"
              className="h-9 w-24"
            >
              {t('cancel')}
            </Button>
            <Button
              isDisabled={isLoading}
              onClick={handleConfirm}
              className="h-9 w-24"
            >
              {t('attach')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};
